import React from "react";
import { Paper, Stack, Typography } from "@mui/material";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import SensorsOffOutlinedIcon from "@mui/icons-material/SensorsOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import _ from "lodash";
import SummaryChip from "../../components/SummaryChip";
import { CHANNEL_LABELS } from "../../components/RecipientsEditor";
import { humanDuration } from "../alertRule/alertRuleConstants";

const scopeLabel = (alert) => {
    if (alert.scope === "stores") {
        const count = (alert.store_ids || []).length;
        return count === 1 ? alert.stores?.[0]?.name || "1 local" : `${count} locales`;
    }
    if (alert.scope === "devices") {
        const count = (alert.device_ids || []).length;
        return `${count} equipo${count === 1 ? "" : "s"}`;
    }
    return "Toda la empresa";
};

// Chips de la tarjeta: qué vigila y con qué tiempos.
export const ConnectivityAlertCardSummary = ({ item, compact = false }) => {
    const alert = item.data || {};
    return (
        <>
            <SummaryChip icon={<VisibilityOutlinedIcon />} label={scopeLabel(alert)} />
            {alert.watch_gateways && (
                <SummaryChip icon={<RouterOutlinedIcon />} label={compact ? `${alert.gateway_after_minutes} min` : `Local sin conexión · ${alert.gateway_after_minutes} min`} color="error" />
            )}
            {alert.watch_devices && (
                <SummaryChip icon={<SensorsOffOutlinedIcon />} label={compact ? `${alert.device_after_minutes} min` : `Sensor sin datos · ${alert.device_after_minutes} min`} color="warning" />
            )}
        </>
    );
};

// Lo que está caído ahora y ya se avisó.
export const connectivityAlertStatusChip = (item) => {
    const alert = item.data || {};
    if (!alert.enabled) return null;
    const offline = alert.open_problems?.offline || 0;
    const noData = alert.open_problems?.no_data || 0;
    if (offline) return { label: offline === 1 ? "1 local sin conexión" : `${offline} locales sin conexión`, color: "error" };
    if (noData) return { label: noData === 1 ? "1 equipo sin datos" : `${noData} equipos sin datos`, color: "warning" };
    return null;
};

const recipientsText = (alert) => {
    const names = (alert.recipients || []).filter((recipient) => recipient.enabled !== false).map((recipient) => recipient.user_name);
    if (alert.notify_company_admins) names.unshift("los administradores");
    return names.length ? `Avisa a ${names.join(", ")}` : "Sin destinatarios";
};

export const connectivityAlertSubtitle = (item) => item.description || recipientsText(item.data || {});

const DetailBox = ({ title, children }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, flex: 1, bgcolor: "rgba(15, 23, 42, 0.02)" }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>{title}</Typography>
        <Stack spacing={0.4} sx={{ mt: 0.75 }}>{children}</Stack>
    </Paper>
);

export const ConnectivityAlertCardDetails = ({ item }) => {
    const alert = item.data || {};
    return (
        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} sx={{ pt: 1.5 }}>
            <DetailBox title="Vigila">
                <Typography variant="body2">{scopeLabel(alert)}</Typography>
                {alert.scope === "stores" && (alert.stores || []).length > 1 && (
                    <Typography variant="caption" color="text.secondary">{_.map(alert.stores, "name").join(", ")}</Typography>
                )}
                {!_.isEmpty(alert.excluded_device_ids) && (
                    <Typography variant="caption" color="text.secondary">
                        Excluye {alert.excluded_device_ids.length} equipo{alert.excluded_device_ids.length === 1 ? "" : "s"}
                    </Typography>
                )}
            </DetailBox>
            <DetailBox title="Avisa">
                {alert.watch_gateways && <Typography variant="body2">Local sin conexión después de {alert.gateway_after_minutes} min</Typography>}
                {alert.watch_devices && <Typography variant="body2">Sensor sin datos después de {alert.device_after_minutes} min</Typography>}
                <Typography variant="caption" color="text.secondary">
                    Repite cada {humanDuration(alert.cooldown_minutes * 60)}{alert.notify_recovery ? " y avisa cuando vuelve" : ""}
                </Typography>
            </DetailBox>
            <DetailBox title="A quién">
                {alert.notify_company_admins && <Typography variant="body2">Administradores de la empresa · Correo</Typography>}
                {(alert.recipients || []).map((recipient) => (
                    <Typography key={`recipient-${recipient.id}`} variant="body2" color={recipient.enabled === false ? "text.secondary" : "text.primary"}>
                        {recipient.user_name} · {CHANNEL_LABELS[recipient.channel] || recipient.channel}
                        {recipient.notify_after_seconds > 0 ? ` · a los ${humanDuration(recipient.notify_after_seconds)}` : ""}
                    </Typography>
                ))}
                {!alert.notify_company_admins && _.isEmpty(alert.recipients) && <Typography variant="body2">Nadie</Typography>}
            </DetailBox>
        </Stack>
    );
};
